import { z } from 'zod';
export const CARBON_PROGRAM_VALUES = [
    'california_lcfs', 'oregon_cfp', 'washington_cfs', 'canada_cfr', 'voluntary',
];
export const CarbonProgramSchema = z.enum(CARBON_PROGRAM_VALUES);
/**
 * Carbon accrual layer (Canada-first launch). Jurisdiction is the
 * REGULATOR a charger registers under, derived from coordinates —
 * ca_federal covers every Canadian province under the federal CFR;
 * ca_bc is split out because BC additionally runs its own LCFS.
 * Distinct from CarbonProgram (the credit scheme money flows through).
 */
export const CARBON_JURISDICTION_VALUES = ['ca_federal', 'ca_bc', 'us_ca'];
export const CarbonJurisdictionSchema = z.enum(CARBON_JURISDICTION_VALUES);
/**
 * WHICH MODE a charger is enrolled in. Recorded on the charger, pinned onto
 * every accrual, and never inferred from whether some other field is empty.
 *
 *   aggregator  EV Initiative claims the credits as the registered network
 *               operator and keeps the proceeds. Needs no host, no ownership
 *               edge and no host consent, because nothing is being assigned
 *               from anyone. Payout resolves to the tenant Connect account.
 *               THE DEFAULT.
 *   shared      The host has opted into revenue sharing. Needs an identified
 *               host (Tenant.owner_user_id) who has accepted the Carbon
 *               Credit Program Terms, because THAT document carries the
 *               assignment of environmental attributes. Payout resolves to
 *               the host.
 *
 * Recording it is the point. Settlement used to infer the payee from
 * host_user_id being null and fall through to the tenant, which conflated
 * two opposite facts: "an owner exists but the mirror never wrote the edge"
 * and "no host is involved because we are claiming these ourselves". The
 * tenant branch must fire because a mode says aggregator, never because a
 * field happened to be empty.
 */
export const CARBON_MODE_VALUES = ['aggregator', 'shared'];
export const CarbonModeSchema = z.enum(CARBON_MODE_VALUES);
/**
 * WHY a session is parked in the deferred pool rather than accrued or
 * marked terminal. Every one of these is RETRYABLE and clears on its own
 * once the underlying condition does — none is a verdict on the session.
 *
 *   no_rate               No CarbonRateVersion is in force at the session's
 *                         start for its jurisdiction. Clears on a publish,
 *                         and on a BACKDATED publish for historic sessions.
 *   epoch_conflict        The charger's registered_at predates the effective
 *                         accrual epoch, so accruing would submit a partial
 *                         window that reads as complete. Clears when the
 *                         epoch is lowered or registered_at is corrected.
 *   shared_host_unresolved  The charger is in shared mode for this session's
 *                         window but its host cannot be resolved. Clears when
 *                         Tenant.owner_user_id is written or the charger goes
 *                         back to aggregator.
 *   error                 The row threw. Kept distinct because a deterministic
 *                         per-document failure looks identical to a transient
 *                         one until you can see it repeat, which is exactly
 *                         what the attempt counter is for.
 *
 * Typed rather than free text so the health surface can aggregate by reason;
 * free text cannot be grouped, which is the whole point of persisting it.
 */
export const CARBON_DEFERRAL_REASON_VALUES = [
    'no_rate', 'epoch_conflict', 'shared_host_unresolved', 'error',
];
export const CarbonDeferralReasonSchema = z.enum(CARBON_DEFERRAL_REASON_VALUES);
/**
 * Lifecycle of one session's accrued credit: accrued (sweeper wrote it)
 * -> submitted (in a partner claim batch) -> realized (partner paid;
 * ONLY this state releases the host share) | rejected.
 * Deliberately not the legacy CarbonCreditStatus below — that enum
 * belongs to the never-consumed scaffold types and has different
 * semantics (no rejected state, a 'traded' tail we don't model).
 */
export const CARBON_ACCRUAL_STATUS_VALUES = ['accrued', 'submitted', 'realized', 'rejected'];
export const CarbonAccrualStatusSchema = z.enum(CARBON_ACCRUAL_STATUS_VALUES);
export const CarbonCreditStatusSchema = z.enum(['pending', 'submitted', 'verified', 'traded']);
export const CarbonCreditSchema = z.object({
    _id: z.string(),
    tenant_id: z.string(),
    program: CarbonProgramSchema,
    period_start: z.coerce.date(),
    period_end: z.coerce.date(),
    total_energy_kwh: z.number().nonnegative(),
    carbon_intensity: z.number().nonnegative(),
    credits_generated: z.number().nonnegative(),
    credit_value_usd: z.number().nonnegative(),
    status: CarbonCreditStatusSchema,
    report_url: z.string().url().optional(),
    created_at: z.coerce.date(),
});
//# sourceMappingURL=carbon.js.map